import { Link2, CheckCircle2, XCircle, Radio, Clock } from 'lucide-react';

const norm = (s) => String(s || '').toLowerCase().replace(/\b(moneyline|ml|to win|yes)\b/g, '').replace(/[^a-z0-9.+-]/g, '');

export const legKey = (leg) => `${leg.espn_event_id || ''}|${norm(leg.selection || leg.team)}`;

export const ticketLabel = (t) => {
  const first = (t.legs?.[0]?.selection || '').replace(/\s*moneyline$/i, '');
  const stake = t.stake ? `$${Number(t.stake).toLocaleString('en-US')}` : '';
  return t.descriptor || [stake, first && `${first} ticket`].filter(Boolean).join(' ') || 'Ticket';
};

// key -> { leg, tickets: [{id,label}] } for picks on 2+ tickets
export const buildSharedMap = (tickets) => {
  const map = {};
  tickets.forEach((t) => (t.legs || []).forEach((leg) => {
    const k = legKey(leg);
    if (!map[k]) map[k] = { leg, tickets: [] };
    if (!map[k].tickets.some((x) => x.id === t.id)) map[k].tickets.push({ id: t.id, label: ticketLabel(t) });
  }));
  return Object.fromEntries(Object.entries(map).filter(([, v]) => v.tickets.length > 1));
};

const STATE = {
  won: { icon: CheckCircle2, cls: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10', verb: 'Helped' },
  lost: { icon: XCircle, cls: 'text-red-300 border-red-500/40 bg-red-500/10', verb: 'Hurt' },
  up: { icon: Radio, cls: 'text-sky-300 border-sky-500/40 bg-sky-500/10', verb: 'Up — helping' },
  down: { icon: Radio, cls: 'text-amber-300 border-amber-500/40 bg-amber-500/10', verb: 'Trailing/tied — hurting' },
  live: { icon: Radio, cls: 'text-zinc-200 border-zinc-600 bg-zinc-800/60', verb: 'Tied — live on' },
  pending: { icon: Clock, cls: 'text-zinc-300 border-zinc-700 bg-zinc-900', verb: 'Riding on' },
};

export const legState = (leg) => {
  const s = String(leg.status || leg.settlement_status || '').toLowerCase();
  if (['won', 'win', 'hit'].includes(s)) return 'won';
  if (['lost', 'loss', 'miss'].includes(s)) return 'lost';
  if (s === 'live' || leg.match_status === 'in') {
    if (leg.color_token === 'blue') return 'up';
    return 'down';
  }
  return 'pending';
};

export const SharedBadge = ({ entry, ticketId }) => {
  if (!entry) return null;
  const others = entry.tickets.filter((t) => t.id !== ticketId).map((t) => t.label);
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-sky-500/40 text-sky-300 bg-sky-500/10"
      title={`Also on: ${others.join(', ')}`} data-testid="shared-leg-badge">
      <Link2 className="w-3 h-3" /> Also on {others.length} other
    </span>
  );
};

export const SharedPicksPanel = ({ sharedMap }) => {
  const entries = Object.values(sharedMap);
  if (!entries.length) return null;
  return (
    <section className="rounded-xl border border-sky-500/30 bg-zinc-900/70 p-3" data-testid="shared-picks-panel">
      <p className="text-xs font-bold uppercase tracking-wider text-sky-300 mb-2 flex items-center gap-1.5">
        <Link2 className="w-3.5 h-3.5" /> Shared picks — one result, several tickets
      </p>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2">
        {entries.map(({ leg, tickets }) => {
          const st = STATE[legState(leg)];
          const Icon = st.icon;
          return (
            <div key={legKey(leg)} className={`rounded-lg border px-3 py-2 ${st.cls}`} data-testid="shared-pick">
              <div className="flex items-center gap-1.5 text-sm font-semibold">
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{(leg.selection || '').replace(/\s*moneyline$/i, '')}</span>
                {(leg.live_score_away != null && leg.live_score_home != null && legState(leg) !== 'pending') && (
                  <span className="ml-auto text-xs font-mono text-zinc-300">{leg.live_score_away}-{leg.live_score_home}</span>
                )}
              </div>
              <p className="text-[11px] mt-0.5" data-testid="shared-pick-effect">
                {st.verb} {tickets.length} tickets: <span className="text-zinc-300">{tickets.map((t) => t.label).join(' · ')}</span>
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// One-line version for the all-in-one board.
export const SharedChips = ({ sharedMap }) => {
  const entries = Object.values(sharedMap);
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 text-[10px]" data-testid="shared-chips">
      <span className="text-sky-300 font-semibold inline-flex items-center gap-1"><Link2 className="w-3 h-3" />Shared:</span>
      {entries.map(({ leg, tickets }) => {
        const k = legState(leg);
        return (
          <span key={legKey(leg)} className={`px-1.5 py-0.5 rounded border ${STATE[k].cls}`} title={`${STATE[k].verb} ${tickets.length} tickets: ${tickets.map((t) => t.label).join(' · ')}`}
            data-testid="shared-chip">
            {(leg.selection || '').replace(/\s*moneyline$/i, '')} ×{tickets.length}{k === 'won' ? ' ✓' : k === 'lost' ? ' ✗' : ''}
          </span>
        );
      })}
    </div>
  );
};
